<template>
    <div class="inline-markdown-editor">
        <c-text-editor
            ref="editor"
            :data-editor-extensions="extensions"
            :readingMode="props.disabled"
            :hideMenuBar="true"
            :placeholder="props.placeholder"
            :darkMode="ui.darkMode.value"
            @c-text-editor:register="registerEditor"
            @c-text-editor:changed="documentChanged"
            @c-text-editor:submit="onSubmit"
        ></c-text-editor>
    </div>
</template>

<script lang="ts" setup>
/**
 * Minimal, chrome-less markdown editor bound to a plain string (value-mode),
 * unlike RichTextEditor which is bound to an Artefact. No toolbar; the one
 * power feature is "/" entity references with typed autocomplete.
 */
import { ref, watch } from "vue";

import { useUIManager } from "@/stores/ui-store";
import "@/wc/text-editor/component/text-editor";

const model = defineModel<string>({ default: "" });
const props = withDefaults(
    defineProps<{
        placeholder?: string;
        submitOnEnter?: boolean;
        disabled?: boolean;
    }>(),
    { placeholder: "", submitOnEnter: false, disabled: false }
);
const emit = defineEmits<{
    submit: [content: string];
}>();

const ui = useUIManager();
const editorHooks = ref<{
    load: (source: string) => void;
    getMarkdown: () => string;
}>();

// Computed once — the web component does not re-configure on extension
// changes, so submitOnEnter is not reactive. submit-on-enter must be FIRST:
// later extensions get higher keydown priority, and the popup menus (slash
// menu, entity autocompletes) must keep handling Enter while open.
const extensions = [
    ...(props.submitOnEnter ? ["submit-on-enter"] : []),
    "inline-insert-menu",
    "mystekz-business-entities",
    "mystekz-domain-terms",
    "mystekz-roles",
    "mystekz-domains",
    "mystekz-products",
    "mystekz-processes",
].join(",");

const registerEditor = (event: CustomEvent) => {
    editorHooks.value = event.detail.value;
    editorHooks.value?.load(model.value ?? "");
};

// External model changes (seed buttons, clear-after-send) reload the editor.
// serialize∘parse is idempotent after one normalization, so comparing against
// the live document breaks the update loop.
watch(model, (value) => {
    if (!editorHooks.value) return;
    if ((value ?? "") !== editorHooks.value.getMarkdown()) {
        editorHooks.value.load(value ?? "");
    }
});

const documentChanged = () => {
    // The changed event fires before the view swaps in the new state —
    // defer the read (same workaround as RichTextEditor).
    setTimeout(() => {
        const markdown = editorHooks.value?.getMarkdown();
        if (markdown !== undefined && markdown !== model.value) {
            model.value = markdown;
        }
    }, 0);
};

const onSubmit = () => {
    // Read the live document rather than the model: the model update from
    // the last keystroke may still be sitting in the deferred timeout.
    emit("submit", editorHooks.value?.getMarkdown() ?? "");
};
</script>

<style lang="scss" scoped>
.inline-markdown-editor {
    display: flex;
    flex-direction: column;

    > * {
        flex: 1;
    }
}
</style>
